import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Ticket, CheckCircle, Clock } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuth, API } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import { Badge } from '../ui/badge';
import { formatAmount } from '../../utils/format';
const CouponsTab = () => {
  const { getAuthHeaders } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 0,
    expiry_date: '',
    max_uses: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      const response = await axios.get(`${API}/coupons`, {
        headers: getAuthHeaders()
      });
      setCoupons(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar cupones'));
    }
  };

  const handleOpenDialog = () => {
    setEditingCoupon(null);
    setFormData({ code: '', discount_type: 'percentage', discount_value: 0, expiry_date: '', max_uses: '' });
    setIsDialogOpen(true);
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      expiry_date: coupon.expiry_date ? new Date(coupon.expiry_date).toISOString().split('T')[0] : '',
      max_uses: coupon.max_uses?.toString() || ''
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setTimeout(() => {
      setEditingCoupon(null);
      setFormData({ code: '', discount_type: 'percentage', discount_value: 0, expiry_date: '', max_uses: '' });
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        code: formData.code.toUpperCase().trim(),
        discount_type: formData.discount_type,
        discount_value: formData.discount_type === 'percentage'
          ? Math.min(100, Math.max(0, parseFloat(formData.discount_value) || 0))
          : Math.max(0, parseFloat(formData.discount_value) || 0),
        expiry_date: formData.expiry_date ? new Date(formData.expiry_date).toISOString() : null,
        max_uses: formData.max_uses ? parseInt(formData.max_uses, 10) : null
      };
      if (editingCoupon) {
        await axios.put(`${API}/coupons/${editingCoupon.id}`, data, { headers: getAuthHeaders() });
        toast.success('Cupón actualizado');
      } else {
        await axios.post(`${API}/coupons`, data, { headers: getAuthHeaders() });
        toast.success('Cupón creado');
      }
      await loadCoupons();
      handleCloseDialog();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar'));
    }
    setLoading(false);
  };

  const handleToggleStatus = async (coupon) => {
    try {
      await axios.put(`${API}/coupons/${coupon.id}`, { is_active: !coupon.is_active }, { headers: getAuthHeaders() });
      toast.success(`Cupón ${coupon.is_active ? 'desactivado' : 'activado'}`);
      loadCoupons();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cambiar estado'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este cupon?')) return;
    try {
      await axios.delete(`${API}/coupons/${id}`, { headers: getAuthHeaders() });
      toast.success('Cupon eliminado');
      loadCoupons();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar'));
    }
  };

  const isExpired = (d) => d && new Date(d) < new Date();
  const isFullyUsed = (c) => c.max_uses && c.current_uses >= c.max_uses;

  const statusBadge = (c) => {
    if (!c.is_active) return <Badge className="bg-gray-100 text-gray-600 rounded-full">Inactivo</Badge>;
    if (isExpired(c.expiry_date)) return <Badge className="bg-red-100 text-red-700 rounded-full">Expirado</Badge>;
    if (isFullyUsed(c)) return <Badge className="bg-orange-100 text-orange-700 rounded-full">Agotado</Badge>;
    return <Badge className="bg-green-100 text-green-700 rounded-full">Activo</Badge>;
  };

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-orange-100">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
            <Ticket className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Cupones de Descuento</h2>
            <p className="text-sm text-gray-600">Crea y gestiona cupones promocionales</p>
          </div>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleOpenDialog} className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Cupon
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingCoupon ? 'Editar Cupon' : 'Nuevo Cupon'}</DialogTitle>
              <DialogDescription>
                {editingCoupon 
                  ? 'Modifica los datos del cupón de descuento'
                  : 'Crea un nuevo cupón para ofrecer descuentos a tus clientes'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Codigo</Label>
                <Input value={formData.code} onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})} placeholder="Ej: VERANO2024" className="rounded-xl font-mono uppercase" required disabled={!!editingCoupon} />
                <p className="text-xs text-gray-500 mt-1">El codigo se guarda en mayusculas y no se puede modificar despues de crear</p>
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={formData.discount_type} onValueChange={v => setFormData({...formData, discount_type: v})}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                    <SelectItem value="coupon">Monto Fijo ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{formData.discount_type === 'percentage' ? 'Valor (%)' : 'Valor ($)'}</Label>
                <Input type="number" step={formData.discount_type === 'percentage' ? '1' : '0.01'} min="0" max={formData.discount_type === 'percentage' ? '100' : undefined} value={formData.discount_value} onChange={e => setFormData({...formData, discount_value: parseFloat(e.target.value) || 0})} required className="rounded-xl" />
              </div>
              <div>
                <Label>Fecha de Expiracion (opcional)</Label>
                <Input type="date" value={formData.expiry_date} onChange={e => setFormData({...formData, expiry_date: e.target.value})} className="rounded-xl" />
              </div>
              <div>
                <Label>Usos Maximos (opcional)</Label>
                <Input type="number" min="1" value={formData.max_uses} onChange={e => setFormData({...formData, max_uses: e.target.value})} placeholder="Ej: 100" className="rounded-xl" />
                <p className="text-xs text-gray-500 mt-1">Dejar vacio para usos ilimitados</p>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={handleCloseDialog} className="flex-1 rounded-xl">Cancelar</Button>
                <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">{editingCoupon ? 'Actualizar' : 'Crear'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {coupons.length === 0 ? (
        <div className="text-center py-12">
          <Ticket className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-2">No hay cupones configurados</p>
          <p className="text-sm text-gray-400">Crea tu primer cupon para empezar a ofrecer descuentos</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {coupons.map(c => (
            <Card key={c.id} className="p-4 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-bold text-lg text-gray-800 font-mono">{c.code}</h3>
                    {statusBadge(c)}
                    <Badge variant="outline" className="text-xs rounded-full">{c.discount_type === 'percentage' ? 'Porcentaje' : 'Monto Fijo'}</Badge>
                  </div>
                  <div className="mt-2 flex items-center gap-4 flex-wrap">
                    <span className="text-2xl font-bold text-orange-600">{c.discount_type === 'percentage' ? `${c.discount_value}%` : `$${formatAmount(c.discount_value)}`}</span>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>{c.current_uses || 0} usado(s){c.max_uses ? ` / ${c.max_uses} max` : ''}</span>
                    </div>
                    {c.expiry_date && (
                      <div className={`flex items-center gap-1 text-sm ${isExpired(c.expiry_date) ? 'text-red-500' : 'text-gray-500'}`}>
                        <Clock className="w-4 h-4" />
                        <span>Expira: {new Date(c.expiry_date).toLocaleDateString('es-ES')}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">{c.is_active ? 'Activo' : 'Inactivo'}</Label>
                    <Switch checked={c.is_active} onCheckedChange={() => handleToggleStatus(c)} />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleEdit(c)} className="rounded-xl"><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(c.id)} className="rounded-xl text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CouponsTab;
